export interface GraphNode {
  id: string;
  name: string;
  description: string;
  x: number;
  y: number;
}

export interface GraphEdge {
  id: string;
  sourceId: string;
  targetId: string;
}

export enum GraphEventAction {
  ENTER = 'enter',
  LEAVE = 'leave',
  CLICK = 'click',
}

export enum GraphEventType {
  GRAPH_NODE = 'graphNode',
  GRAPH_EDGE = 'graphEdge',
}

export interface GraphNodeEvent {
  action: GraphEventAction;
  node: GraphNode;
  event?: React.MouseEvent;
}

export interface GraphEdgeEvent {
  action: GraphEventAction;
  edge: GraphEdge;
  event?: React.MouseEvent;
}

export enum ItemState {
  HIGHLIGHTED,
  SUBDUED,
  DEFAULT,
  SELECTED,
}

export interface GraphNodeProps {
  key: number;
  node: GraphNode;
  x: number;
  y: number;
  state: ItemState[];
  zoom: number;
  shouldDisableTransitions: boolean;
  onClick: (interaction?: GraphNodeEvent) => void;
  onMouseEnter: (interaction?: GraphNodeEvent) => void;
  onMouseLeave: (interaction?: GraphNodeEvent) => void;
}

export interface GraphEdgeProps {
  key: number;
  edge: GraphEdge;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  state: ItemState[];
  zoom: number;
  onClick: (interaction?: GraphEdgeEvent) => void;
  onMouseEnter: (interaction?: GraphEdgeEvent) => void;
  onMouseLeave: (interaction?: GraphEdgeEvent) => void;
}

export const TRANSPARENT = `rgba(0, 0, 0, 0)`;

export const truncate = (str: string, length = 45) => {
  if (str.length > length) {
    return `${str.slice(0, length)}...`;
  }

  return str;
};

export const buildRelationshipMap = (graphNodes: GraphNode[], graphEdges: GraphEdge[]) => {
  // Create a map to quickly determine which nodes/edges are one
  // hop away from any given node/edge:
  const relationshipMap: { [id: string]: string[] } = {};
  graphNodes.forEach((n) => {
    // Get all edges that are connected to the node:
    const edges = graphEdges.filter((e) => [e.sourceId, e.targetId].includes(n.id));
    // Get all nodes connected to these edges
    const relatedNodeIds = edges.map((e) => (e.sourceId === n.id ? e.targetId : e.sourceId));
    // Given node ID, return edge IDs and the connected node IDs
    relationshipMap[n.id] = edges.map((e) => e.id).concat(relatedNodeIds);
  });

  graphEdges.forEach((edge) => {
    // Given edge ID, return node IDs connected
    relationshipMap[edge.id] = [edge.sourceId, edge.targetId];
  });

  return relationshipMap;
};
